/* eslint-disable complexity */
import { Vector3 } from 'three';
import { IBuildingProviderContext, ITexture } from '../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../hooks/useUndoRedo';

import AttributeModel from '../../models/standard/AttributeModel';
import CameraPositionSpriteModel from '../../models/standard/CameraPositionSpriteModel';
import CompassModel from '../../models/standard/CompassModel';
import ElementModel from '../../models/standard/ElementModel';
import FloorModel from '../../models/standard/FloorModel';
import HotspotModel from '../../models/standard/HotspotModel';
import Model2DModel from '../../models/standard/Model2DModel';
import { IObject } from '../../models/standard/ObjectModel';
import PlanModel from '../../models/standard/PlanModel';
import ProductModel from '../../models/standard/ProductModel';
import RoomModel from '../../models/standard/RoomModel';
import SimpleMeasurementModel from '../../models/standard/SimpleMeasurementModel';
import SpaceModel from '../../models/standard/SpaceModel';

import PhysicalBoundaryModel, { BoundaryEdge } from '../../models/standard/PhysicalBoundary';
import StandardWallModel, { IStandardWall } from '../../models/standard/StandardWallModel';

import { APP_VERSION, JSON_VERSION } from '../constants';
import { NonFetchableEntity } from '../planJsonExportImportUtils';
import deserializeHotspot from './deserializeHotspot';
import deserializeMeasurement from './deserializeMeasurement';
import { fetchProductById } from './fetchProductById';
import { fetchTextureFromApi } from './fetchTextureFromApi';
import { instantiateElementAsync } from './instantiateElementAsync';
import { instantiateModel2DAsync } from './instantiateModel2DAsync';
import { instantiateTiles } from './instantiateTiles';
import { instantiateWall } from './instantiateWall';

type ImportV1Result = Promise<{
  plan: PlanModel;
  building: IBuildingProviderContext;
  measurements: SimpleMeasurementModel[];
  cameraPositionSprites: CameraPositionSpriteModel[];
  hotspots: HotspotModel[];
  nonFetchableEntities: Array<NonFetchableEntity>;
}>;

const asArray = <T>(v: any): T[] => (Array.isArray(v) ? (v as T[]) : []);
const strId = (v: any): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return s.length ? s : '-1';
};
const keyOf = (x: any) => String(x?.uuid ?? x?.id ?? x?.productId ?? x?.modelId ?? '');

// --- GEOMETRY HELPERS (Neu für Migration) ---
const EPS = 1e-4;
const samePt = (a: Vector3, b: Vector3) => Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;

function signedArea2D(points: Vector3[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y - points[j].x * points[i].y;
  }
  return area / 2;
}

function intersectLines2D(p1: Vector3, dir1: Vector3, p2: Vector3, dir2: Vector3): Vector3 | null {
  const cross = dir1.x * dir2.y - dir1.y * dir2.x;
  if (Math.abs(cross) < EPS) return null;
  const t = ((p2.x - p1.x) * dir2.y - (p2.y - p1.y) * dir2.x) / cross;
  return new Vector3(p1.x + t * dir1.x, p1.y + t * dir1.y, 0);
}

function orderWallsIntoLoop(walls: IStandardWall[]): IStandardWall[] {
  if (walls.length < 3) return walls;
  const unused = new Set(walls.map((w) => w.uuid));
  const byUuid = new Map(walls.map((w) => [w.uuid, w]));
  const first = walls[0];
  const ordered: IStandardWall[] = [first];
  unused.delete(first.uuid);

  const start = first.startPoint.clone();
  let cursor = first.endPoint.clone();

  while (unused.size) {
    let next: IStandardWall | undefined;
    for (const uuid of unused) {
      const w = byUuid.get(uuid)!;
      if (samePt(w.startPoint, cursor) || samePt(w.endPoint, cursor)) {
        next = w;
        break;
      }
    }
    if (!next) break;
    ordered.push(next);
    unused.delete(next.uuid);
    cursor = samePt(next.startPoint, cursor) ? next.endPoint.clone() : next.startPoint.clone();
    if (unused.size === 0 && samePt(cursor, start)) return ordered;
  }
  return walls;
}

export const importPlanJsonV1 = async (jsonString: string, isFilesApiAvailable: boolean = true): ImportV1Result => {
  const data = JSON.parse(jsonString ?? '{}');
  const planData = data?.plan ?? {};
  const previousNonFetchableEntities: Array<NonFetchableEntity> = asArray(planData?.nonFetchableEntities);

  let nonFetchableEntities: Array<NonFetchableEntity> = [];

  const buildingData = planData?.building ?? {};
  const levelData = (Array.isArray(buildingData?.buildingLevels) && buildingData.buildingLevels[0]) ||
    (Array.isArray((buildingData as any)?.levels) && (buildingData as any).levels[0]) || { rooms: [] };

  const rawRoom = (Array.isArray(levelData?.rooms) && levelData.rooms[0]) || null;
  const v2FirstSpace = rawRoom?.spaces?.[0];

  // Robust Fallback: Ensure we always have a base object
  const roomData: any =
    v2FirstSpace && typeof v2FirstSpace === 'object'
      ? {
          title: rawRoom?.title ?? '',
          uuid: rawRoom?.uuid,
          attributes: rawRoom?.attributes ?? [],
          walls: v2FirstSpace?.walls ?? [],
          products: v2FirstSpace?.products ?? [],
          elements: v2FirstSpace?.elements ?? [],
          models2D: v2FirstSpace?.models2D ?? [],
          floor: v2FirstSpace?.floor ?? undefined,
          spaceUuid: v2FirstSpace?.uuid,
        }
      : rawRoom ?? {
          title: '',
          uuid: undefined,
          attributes: [],
          walls: [],
          products: [],
          elements: [],
          models2D: [],
          floor: undefined,
          spaceUuid: undefined,
        };

  const floorData: any = roomData?.floor ?? (Array.isArray(roomData?.floors) ? roomData.floors[0] : undefined) ?? { texture: null, tiles: null, attributes: [], uuid: undefined };
  const auth = true;

  const rawWalls = asArray<any>(roomData?.walls);

  if (rawWalls.length >= 3) {
    const orderedRawWalls: any[] = [];
    const remaining = [...rawWalls];
    const currentWall = remaining.shift();
    orderedRawWalls.push({ wall: currentWall, forward: true });

    let currentPt = new Vector3(currentWall.endPoint.x, currentWall.endPoint.y, 0);

    while (remaining.length > 0) {
      let nextIdx = -1;
      let forward = true;

      for (let k = 0; k < remaining.length; k++) {
        const w = remaining[k];
        const dStart = Math.hypot(w.startPoint.x - currentPt.x, w.startPoint.y - currentPt.y);
        const dEnd = Math.hypot(w.endPoint.x - currentPt.x, w.endPoint.y - currentPt.y);

        if (dStart < 1e-2) {
          nextIdx = k;
          forward = true;
          break;
        } else if (dEnd < 1e-2) {
          nextIdx = k;
          forward = false;
          break;
        }
      }

      if (nextIdx === -1) break; 
      
      const nextWall = remaining.splice(nextIdx, 1)[0];
      orderedRawWalls.push({ wall: nextWall, forward });
      currentPt = forward 
        ? new Vector3(nextWall.endPoint.x, nextWall.endPoint.y, 0)
        : new Vector3(nextWall.startPoint.x, nextWall.startPoint.y, 0);
    }

    if (orderedRawWalls.length === rawWalls.length) {
      const vertices = orderedRawWalls.map(o => o.forward 
        ? new Vector3(o.wall.startPoint.x, o.wall.startPoint.y, 0)
        : new Vector3(o.wall.endPoint.x, o.wall.endPoint.y, 0)
      );

      const area = signedArea2D(vertices);
      const isCW = area <= 0;

      const offsetLines = vertices.map((p, i) => {
        const nextP = vertices[(i + 1) % vertices.length];
        const dir = new Vector3().subVectors(nextP, p).normalize();
        
        const outwardNormal = isCW ? new Vector3(-dir.y, dir.x, 0) : new Vector3(dir.y, -dir.x, 0);
        const depth = orderedRawWalls[i].wall.depth > 0 ? orderedRawWalls[i].wall.depth : 12;
        const shiftVec = outwardNormal.clone().multiplyScalar(depth / 2);

        return { p: p.clone().add(shiftVec), dir, shiftVec };
      });

      const newVertices = [];
      for (let i = 0; i < vertices.length; i++) {
        const prev = offsetLines[(i - 1 + vertices.length) % vertices.length];
        const curr = offsetLines[i];
        const hit = intersectLines2D(prev.p, prev.dir, curr.p, curr.dir);
        newVertices.push(hit || curr.p.clone());
      }

      for (let i = 0; i < orderedRawWalls.length; i++) {
        const o = orderedRawWalls[i];
        const newStart = newVertices[i];
        const newEnd = newVertices[(i + 1) % newVertices.length];
        const shiftVec = offsetLines[i].shiftVec;

        o.wall.startPoint.x = o.forward ? newStart.x : newEnd.x;
        o.wall.startPoint.y = o.forward ? newStart.y : newEnd.y;
        o.wall.endPoint.x = o.forward ? newEnd.x : newStart.x;
        o.wall.endPoint.y = o.forward ? newEnd.y : newStart.y;

        const translateShape = (shapeList: any[]) => {
          if (!shapeList) return;
          shapeList.forEach(shape => {
            if (shape.position) {
              shape.position.x += shiftVec.x;
              shape.position.y += shiftVec.y;
            }
          });
        };

        translateShape(o.wall.windows);
        translateShape(o.wall.doors);
        translateShape(o.wall.radiators);
        if (o.wall.sides?.A?.radiators) translateShape(o.wall.sides.A.radiators);
        if (o.wall.sides?.B?.radiators) translateShape(o.wall.sides.B.radiators);
      }
    }
  }

  // --- WALLS ---
  const walls = rawWalls.map((wallData: any) => instantiateWall(wallData));

  // --- FLOOR TEXTURE ---
  const isLegacyTexture = floorData?.texture?.id === -1 && !!floorData?.texture?.color;
  const floorTexturePromise = (async () => {
    if (!floorData?.texture || isFilesApiAvailable === false) {
      return { texture: null, fetchingSuccesful: true, neededToFetchTexture: false, subType: undefined };
    }
    if (isLegacyTexture) {
      return {
        texture: { id: -1, name: '', material: null, color: floorData.texture.color, rotation: 0, offset: 0 } as ITexture,
        fetchingSuccesful: true,
        neededToFetchTexture: false,
        subType: undefined,
      };
    }
    if (auth) {
      let floorTextureDataToFetch = floorData.texture;
      const prev = previousNonFetchableEntities.find((nFE) => nFE.modelType === 'floor' && nFE.type === 'texture');
      if (prev?.entityData?.texture) {
        floorTextureDataToFetch = prev.entityData.texture;
      }
      return await fetchTextureFromApi(floorTextureDataToFetch, isFilesApiAvailable);
    }
    return { texture: null, fetchingSuccesful: false, neededToFetchTexture: false, subType: undefined };
  })();

  let rawProducts = asArray<any>(roomData?.products);
  const prevNFProducts = previousNonFetchableEntities.filter((nfE) => nfE.modelType === 'product' && nfE.entityData !== undefined).map((nfE) => nfE.entityData);
  const prevNFProductKeys = new Set(prevNFProducts.map(keyOf));
  rawProducts = rawProducts.filter((p: any) => !prevNFProductKeys.has(keyOf(p))).concat(prevNFProducts);
  const productPromises = rawProducts.map((prodData: any) => {
    const prodId = prodData?.id ?? prodData?.productId;
    const prodIdStr = strId(prodId);
    if (prodIdStr === '-1')
      return Promise.resolve({
        objectType: 'notFetchableEntity',
        modelType: 'product',
        type: 'modelData',
        id: strId(prodData?.uuid ?? prodData?.id),
        subType: undefined,
        entityData: prodData,
      } as NonFetchableEntity);
    return auth && isFilesApiAvailable
      ? fetchProductById(prodId, prodData.position, prodData.rotation, prodData, prodData.texture)
      : Promise.resolve({
          objectType: 'notFetchableEntity',
          modelType: 'product',
          type: 'modelData',
          id: prodIdStr,
          subType: undefined,
          entityData: prodData,
        } as NonFetchableEntity);
  });

  let rawElements = asArray<any>(roomData?.elements);
  const prevNFElements = previousNonFetchableEntities.filter((nfE) => nfE.modelType === 'element' && nfE.entityData !== undefined).map((nfE) => nfE.entityData);
  const prevNFElementKeys = new Set(prevNFElements.map(keyOf));
  rawElements = rawElements.filter((e: any) => !prevNFElementKeys.has(keyOf(e))).concat(prevNFElements);
  const elementPromises = rawElements.map((elemData: any) => instantiateElementAsync(elemData, isFilesApiAvailable));

  let rawModels2D = asArray<any>(roomData?.models2D);
  const prevNFModels2D = previousNonFetchableEntities.filter((nfE) => nfE.modelType === 'model2D' && nfE.entityData !== undefined).map((nfE) => nfE.entityData);
  const prevNFModel2DKeys = new Set(prevNFModels2D.map(keyOf));
  rawModels2D = rawModels2D.filter((m: any) => !prevNFModel2DKeys.has(keyOf(m))).concat(prevNFModels2D);
  const model2DPromises = rawModels2D.map((modelData: any) => {
    const modelId = modelData?.id ?? modelData?.modelId;
    const modelIdStr = strId(modelId);
    if (auth && isFilesApiAvailable && modelIdStr !== '-1') return instantiateModel2DAsync(modelId, modelData.position, modelData.rotation, modelData.texture, modelData);
    return Promise.resolve({
      element: new Model2DModel(modelId ?? 0, '', '', '', 0, 0, 0, {} as IObject, new Vector3(0, 0, 0), 0, null),
      modelFetchingSuccesful: false,
      neededToFetchFromFilesApi: false,
      neededToFetchTexture: false,
      fetchingSuccesful: false,
      textureFetchingSuccesful: false,
      modelId: modelIdStr,
      textureId: strId(modelData?.texture?.id),
      modelData,
    });
  });

  const hotspotPromises = Array.isArray(planData?.annotations?.[0]?.hotspots) ? planData.annotations[0].hotspots.map(deserializeHotspot) : [];

  const [floorTextureResult, fetchedProducts, elementInstantiations, model2DInstantiations, hotspots] = await Promise.all([
    floorTexturePromise,
    Promise.all(productPromises),
    Promise.all(elementPromises),
    Promise.all(model2DPromises),
    Promise.all(hotspotPromises),
  ]);

  const floorTexture = (floorTextureResult.texture ?? null) as ITexture | null;
  if (!floorTextureResult.fetchingSuccesful && floorTextureResult.neededToFetchTexture) {
    nonFetchableEntities.push({
      objectType: 'notFetchableEntity',
      modelType: 'floor',
      type: 'texture',
      subType: floorTextureResult.subType,
      id: strId(floorData?.texture?.id),
      entityData: floorData,
    });
  }

  const products: ProductModel[] = [];
  for (const result of fetchedProducts as any[]) {
    if (result?.objectType === 'notFetchableEntity') {
      nonFetchableEntities.push(result as NonFetchableEntity);
      continue;
    }
    if (result && typeof result === 'object' && 'product' in result) {
      products.push(result.product as ProductModel);
      if (!result.textureFetchingSuccesful && result.textureId && String(result.textureId) !== '-1')
        nonFetchableEntities.push({
          objectType: 'notFetchableEntity',
          modelType: 'product',
          type: 'texture',
          id: strId(result.textureId),
          subType: result.type,
          entityData: result.entityData,
        });
      continue;
    }
    products.push(result as ProductModel);
  }

  const elements: ElementModel[] = (elementInstantiations as any[]).map((inst) => inst.element as ElementModel);
  const elementTextureFailures: Array<NonFetchableEntity> = (elementInstantiations as any[])
    .filter((e) => String(e.textureId) !== String(-1) && e.neededToFetchTexture && !e.fetchingSuccesful)
    .map((failed) => ({
      objectType: 'notFetchableEntity',
      modelType: 'element',
      type: 'texture',
      id: strId(failed.textureId),
      subType: undefined,
      entityData: failed.elementData,
    }));
  nonFetchableEntities = nonFetchableEntities.concat(elementTextureFailures);

  const models2D: Model2DModel[] = [];
  for (const result of model2DInstantiations as any[]) {
    if (result?.model2D) {
      models2D.push(result.model2D);
      if (!result.textureFetchingSuccesful && result.textureId && String(result.textureId) !== '-1')
        nonFetchableEntities.push({
          objectType: 'notFetchableEntity',
          modelType: 'model2D',
          type: 'texture',
          id: strId(result.textureId),
          subType: undefined,
          entityData: result.modelData,
        });
    } else {
      nonFetchableEntities.push({
        objectType: 'notFetchableEntity',
        modelType: 'model2D',
        type: 'modelData',
        id: strId(result?.modelId),
        subType: undefined,
        entityData: result?.modelData,
      });
    }
  }

  // --- FLOOR & SPACE GUARANTEE ---
  const safeFloorData = floorData && typeof floorData === 'object' ? floorData : {};
  const tiles = safeFloorData?.tiles ? instantiateTiles(safeFloorData.tiles) : undefined;

  // 1. Create Floor (Guaranteed)
  const floor = new FloorModel(
    tiles,
    floorTexture,
    asArray<any>(safeFloorData?.attributes).map((a: any) => new AttributeModel(a.id, a.key, a.value, a.predecessorId, a.type, a.unit, a.hidden, a.forced)),
    safeFloorData?.uuid,
  );

  // 2. Create Boundaries (Guaranteed)
  const boundaries: PhysicalBoundaryModel[] = [];
  const standardWalls = walls.filter((w) => w instanceof StandardWallModel) as IStandardWall[];
  if (standardWalls.length >= 3) {
    const orderedWalls = orderWallsIntoLoop(standardWalls);
    const isClosed =
      samePt(orderedWalls[0].startPoint, orderedWalls[orderedWalls.length - 1].endPoint) || samePt(orderedWalls[0].startPoint, orderedWalls[orderedWalls.length - 1].startPoint);

    if (isClosed) {
      const boundary = new PhysicalBoundaryModel(orderedWalls as any);
      const edges: BoundaryEdge[] = [];
      let cursor = orderedWalls[0].startPoint.clone();
      for (const w of orderedWalls) {
        const reversed = !samePt(w.startPoint, cursor);
        cursor = reversed ? w.startPoint.clone() : w.endPoint.clone();
        edges.push(new BoundaryEdge(w, 0, 1, 'A', reversed));
      }
      boundary.setEdges(edges);
      boundaries.push(boundary);
    }
  }

  // 3. Create Space (Guaranteed)
  const space = new SpaceModel(
    floor,
    products,
    elements,
    models2D,
    boundaries,
    walls, // space walls
  );

  // Force floor link just in case
  const spaceAny = space as any;
  if (spaceAny.floor === null || spaceAny.floor === undefined) spaceAny.floor = floor;

  const spaceUuid = roomData?.spaceUuid ?? floorData?.spaceUuid ?? undefined;
  if (spaceUuid) space.setUUID(spaceUuid);

  // 4. Create Room (Guaranteed)
  const room = new RoomModel(
    roomData?.title ?? '',
    [space],
    asArray<any>(roomData?.attributes).map((a: any) => new AttributeModel(a.id, a.key, a.value, a.predecessorId, a.type, a.unit, a.hidden, a.forced)),
  );
  if (roomData?.uuid) room.setUUID(roomData.uuid);

  const compassModel = new CompassModel(buildingData?.compass?.rotation ?? 0);

  // 5. Final Building Object (Guaranteed Rooms Array)
  const building: IBuildingProviderContext = {
    compass: compassModel,
    rooms: [room], 
    walls,
    products: [],
    elements: [],
    models2D: [],
    nonFetchableEntities,
    isSetByUndoRedo: false,
    lastAction: EHistoryAction.UNKNOWN,
    relations: [],
  };

  const measurements: SimpleMeasurementModel[] = Array.isArray(planData?.annotations?.[0]?.measurements) ? planData.annotations[0].measurements.map(deserializeMeasurement) : [];
  const cameraPositionSprites: CameraPositionSpriteModel[] = Array.isArray(planData?.annotations?.[0]?.cameraPositionSprites)
    ? planData.annotations[0].cameraPositionSprites.map(
        (spriteData: any) => new CameraPositionSpriteModel(spriteData.id, new Vector3(spriteData.position.x, spriteData.position.y, spriteData.position.z), spriteData.spaceUuid ?? null),
      )
    : [];

  const plan = new PlanModel(
    planData?.name,
    planData?.id,
    planData?.kojeId,
    planData?.kojeLevel,
    planData?.eventId,
    planData?.dataObjectId,
    planData?.showroomId,
    APP_VERSION,
    building,
    JSON_VERSION,
    '',
  );

  return { plan, building, measurements, cameraPositionSprites, hotspots, nonFetchableEntities };
};
